import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Divider,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField
} from '@material-ui/core';
import {
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Flag as FlagIcon,
  SwapHoriz as MoveIcon,
  Schedule as ScheduleIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { i18n } from '../../translate/i18n';
// import { DatePicker } from '@material-ui/pickers';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  actionCard: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main + '05',
    },
  },
  selectedAction: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + '10',
  },
  actionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  formControl: {
    minWidth: 200,
    marginBottom: theme.spacing(1),
  },
  selectedTasksChip: {
    margin: theme.spacing(0.5),
  },
}));

const KanbanBulkActions = ({ open, onClose, selectedTasks, onBulkAction }) => {
  const classes = useStyles();
  const [selectedAction, setSelectedAction] = useState('');
  const [actionData, setActionData] = useState({});

  const bulkActions = [
    {
      id: 'assign',
      title: i18n.t('kanban.bulk.bulkAssign'),
      description: i18n.t('kanban.bulk.assignDescription'),
      icon: <AssignmentIcon className={classes.actionIcon} />,
      fields: [
        {
          name: 'userId',
          type: 'select',
          label: i18n.t('kanban.bulk.selectUser'),
          options: [] // This would be populated with actual users
        }
      ]
    },
    {
      id: 'move',
      title: i18n.t('kanban.bulk.bulkMove'),
      description: i18n.t('kanban.bulk.moveDescription'),
      icon: <MoveIcon className={classes.actionIcon} />,
      fields: [
        {
          name: 'targetTag',
          type: 'select',
          label: i18n.t('kanban.bulk.selectTag'),
          options: [] // This would be populated with actual tags
        }
      ]
    },
    {
      id: 'priority',
      title: i18n.t('kanban.bulk.bulkPriority'),
      description: i18n.t('kanban.bulk.priorityDescription'),
      icon: <FlagIcon className={classes.actionIcon} />,
      fields: [
        {
          name: 'priority',
          type: 'select',
          label: i18n.t('kanban.tasks.priority'),
          options: [
            { value: 'low', label: i18n.t('kanban.tasks.priority.low') },
            { value: 'medium', label: i18n.t('kanban.tasks.priority.medium') },
            { value: 'high', label: i18n.t('kanban.tasks.priority.high') },
            { value: 'urgent', label: i18n.t('kanban.tasks.priority.urgent') }
          ]
        }
      ]
    },
    {
      id: 'dueDate',
      title: i18n.t('kanban.bulk.bulkDueDate'),
      description: i18n.t('kanban.bulk.dueDateDescription'),
      icon: <ScheduleIcon className={classes.actionIcon} />,
      fields: [
        {
          name: 'dueDate',
          type: 'date',
          label: i18n.t('kanban.tasks.dueDate')
        }
      ]
    },
    {
      id: 'delete',
      title: i18n.t('kanban.bulk.bulkDelete'),
      description: i18n.t('kanban.bulk.deleteDescription'),
      icon: <DeleteIcon className={classes.actionIcon} style={{ color: '#f44336' }} />,
      fields: [],
      dangerous: true
    }
  ];

  const handleActionSelect = (actionId) => {
    setSelectedAction(actionId);
    setActionData({});
  };

  const handleFieldChange = (fieldName, value) => {
    setActionData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleExecute = async () => {
    if (!selectedAction) return;

    const action = bulkActions.find(a => a.id === selectedAction);
    
    if (action.dangerous) {
      const confirmed = window.confirm(
        i18n.t('kanban.bulk.confirmDangerous', { 
          count: selectedTasks.length,
          action: action.title 
        })
      );
      if (!confirmed) return;
    }

    try {
      await onBulkAction(selectedAction, actionData);
      handleClose();
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  const handleClose = () => {
    setSelectedAction('');
    setActionData({});
    onClose();
  };

  const renderActionFields = () => {
    const action = bulkActions.find(a => a.id === selectedAction);
    if (!action || !action.fields.length) return null;

    return (
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {i18n.t('kanban.bulk.actionSettings')}
        </Typography>
        {action.fields.map(field => (
          <Box key={field.name} marginBottom={2}>
            {field.type === 'select' && (
              <FormControl className={classes.formControl}>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  value={actionData[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                >
                  {field.options.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {field.type === 'date' && (
              <TextField
                label={field.label}
                type="date"
                value={actionData[field.name] ? actionData[field.name].toISOString().split('T')[0] : ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value ? new Date(e.target.value) : null)}
                variant="outlined"
                className={classes.formControl}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            {field.type === 'text' && (
              <TextField
                label={field.label}
                value={actionData[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                className={classes.formControl}
                fullWidth
              />
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const selectedAction_obj = bulkActions.find(a => a.id === selectedAction);
  const canExecute = selectedAction && (
    !selectedAction_obj?.fields.length || 
    selectedAction_obj.fields.every(field => actionData[field.name])
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {i18n.t('kanban.bulk.title')} ({selectedTasks.length} {i18n.t('kanban.bulk.tasksSelected')})
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.bulk.selectedTasks')}
          </Typography>
          <Box>
            {selectedTasks.slice(0, 10).map(taskId => (
              <Chip
                key={taskId}
                label={`#${taskId}`}
                size="small"
                className={classes.selectedTasksChip}
              />
            ))}
            {selectedTasks.length > 10 && (
              <Chip
                label={`+${selectedTasks.length - 10} ${i18n.t('kanban.bulk.moreTasks')}`}
                size="small"
                className={classes.selectedTasksChip}
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        <Divider />

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.bulk.selectAction')}
          </Typography>
          <Grid container spacing={2}>
            {bulkActions.map(action => (
              <Grid item xs={12} sm={6} key={action.id}>
                <Box
                  className={`${classes.actionCard} ${
                    selectedAction === action.id ? classes.selectedAction : ''
                  }`}
                  onClick={() => handleActionSelect(action.id)}
                  style={{
                    borderColor: action.dangerous ? '#f44336' : undefined
                  }}
                >
                  <Box display="flex" alignItems="center" marginBottom={1}>
                    {action.icon}
                    <Typography variant="h6" style={{ 
                      color: action.dangerous ? '#f44336' : undefined 
                    }}>
                      {action.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {action.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {renderActionFields()}

        {selectedAction && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {i18n.t('kanban.bulk.confirmAction')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t('kanban.bulk.willAffect', { 
                count: selectedTasks.length,
                action: selectedAction_obj?.title 
              })}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {i18n.t('kanban.cancel')}
        </Button>
        <Button
          onClick={handleExecute}
          color={selectedAction_obj?.dangerous ? 'secondary' : 'primary'}
          variant="contained"
          disabled={!canExecute}
        >
          {i18n.t('kanban.bulk.execute')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanBulkActions;